import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Search, Menu, X, Youtube } from 'lucide-react';
import { Button } from '@/components/ui/button';
import LanguageSwitcher from './LanguageSwitcher';

const Navigation = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-black/40 border-b border-white/10">
      <div className="max-w-7xl mx-auto px-8 md:px-16">
        <div className="flex items-center justify-between h-24 py-2">
          <Link
            to="/"
            className="flex items-center gap-3 group"
            data-testid="nav-home-link"
          >
            <img 
              src="/images/logo.png" 
              alt="The Movie Matrix" 
              className="w-12 h-12 object-contain group-hover:scale-110 transition-transform duration-300"
            />
            <div className="flex flex-col">
              <span className="font-anton text-xl md:text-2xl text-[#7FFF7F] uppercase tracking-wide leading-tight">
                The Movie Matrix
              </span>
              <span className="text-sm md:text-base text-white font-bold uppercase tracking-widest leading-tight">
                Trailers
              </span>
            </div>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            <Link
              to="/"
              data-testid="nav-home"
              className={`text-base font-medium transition-colors duration-300 ${
                isActive('/')
                  ? 'text-[#7FFF7F]'
                  : 'text-gray-300 hover:text-white'
              }`}
            >
              {t('nav.home')}
            </Link>
            <Link
              to="/past"
              data-testid="nav-past"
              className={`text-base font-medium transition-colors duration-300 ${
                isActive('/past')
                  ? 'text-[#7FFF7F]'
                  : 'text-gray-300 hover:text-white'
              }`}
            >
              {t('nav.past')}
            </Link>
            <Link
              to="/present"
              data-testid="nav-present"
              className={`text-base font-medium transition-colors duration-300 ${
                isActive('/present')
                  ? 'text-[#7FFF7F]'
                  : 'text-gray-300 hover:text-white'
              }`}
            >
              {t('nav.present')}
            </Link>
            <Link
              to="/future"
              data-testid="nav-future"
              className={`text-base font-medium transition-colors duration-300 ${
                isActive('/future')
                  ? 'text-[#7FFF7F]'
                  : 'text-gray-300 hover:text-white'
              }`}
            >
              {t('nav.future')}
            </Link>
            <Link
              to="/browse"
              data-testid="nav-browse"
              className={`text-base font-medium transition-colors duration-300 ${
                isActive('/browse')
                  ? 'text-[#7FFF7F]'
                  : 'text-gray-300 hover:text-white'
              }`}
            >
              {t('nav.browse')}
            </Link>
            <Link
              to="/search"
              data-testid="nav-search"
              className={`text-base font-medium transition-colors duration-300 ${
                isActive('/search')
                  ? 'text-[#7FFF7F]'
                  : 'text-gray-300 hover:text-white'
              }`}
            >
              <Search className="w-5 h-5" />
            </Link>
            <a
              href="https://www.youtube.com/@TheMovieMatrixTV"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-base font-medium text-gray-300 hover:text-[#7FFF7F] transition-colors duration-300"
              data-testid="nav-youtube"
            >
              <Youtube className="w-5 h-5" />
              <span className="hidden lg:inline">{t('nav.channel')}</span>
            </a>
            <LanguageSwitcher />
          </div>

          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-button"
          >
            {mobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </Button>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden py-4 space-y-4" data-testid="mobile-menu">
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className={`block py-2 text-base font-medium ${
                isActive('/') ? 'text-[#7FFF7F]' : 'text-gray-300'
              }`}
            >
              {t('nav.home')}
            </Link>
            <Link
              to="/past"
              onClick={() => setMobileMenuOpen(false)}
              className={`block py-2 text-base font-medium ${
                isActive('/past') ? 'text-[#7FFF7F]' : 'text-gray-300'
              }`}
            >
              {t('nav.past')}
            </Link>
            <Link
              to="/present"
              onClick={() => setMobileMenuOpen(false)}
              className={`block py-2 text-base font-medium ${
                isActive('/present') ? 'text-[#7FFF7F]' : 'text-gray-300'
              }`}
            >
              {t('nav.present')}
            </Link>
            <Link
              to="/future"
              onClick={() => setMobileMenuOpen(false)}
              className={`block py-2 text-base font-medium ${
                isActive('/future') ? 'text-[#7FFF7F]' : 'text-gray-300'
              }`}
            >
              {t('nav.future')}
            </Link>
            <Link
              to="/browse"
              onClick={() => setMobileMenuOpen(false)}
              className={`block py-2 text-base font-medium ${
                isActive('/browse') ? 'text-[#7FFF7F]' : 'text-gray-300'
              }`}
            >
              {t('nav.browse')}
            </Link>
            <Link
              to="/search"
              onClick={() => setMobileMenuOpen(false)}
              className={`block py-2 text-base font-medium ${
                isActive('/search') ? 'text-[#7FFF7F]' : 'text-gray-300'
              }`}
            >
              {t('nav.search')}
            </Link>
            <a
              href="https://www.youtube.com/@TheMovieMatrixTV"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-2 py-2 text-base font-medium text-gray-300"
            >
              <Youtube className="w-5 h-5" />
              YouTube {t('nav.channel')}
            </a>
            <div className="pt-2">
              <LanguageSwitcher />
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;